import { Controller } from "@hotwired/stimulus";
import { sectionRenderer } from "@theme/section-renderer";
import { requestIdleCallback } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";

class PaginationController extends Controller {
  static targets = [
    "productGrid",
    "loadMoreButton",
    "listSummary",
    "itemsViewed",
  ];

  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  isLoading = false;

  connect() {
    this.#fetchPage("next");

    // Listen for filter updates to clear cached pages
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  disconnect() {
    // Remove the filter update listener
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  async renderNextPage() {
    const grid = this.productGridTarget;

    if (!grid) {
      return;
    }

    const nextPage = this.#getPage("next");

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      return;
    }
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage("next");

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) {
        return;
      }
    }

    grid.append(...nextPageItemElements);
    grid.dataset.currentPage = nextPage.page.toString();

    //this.#aspectRatioHelper.processNewElements();

    //history.pushState("", "", nextPage.url.toString());

    document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

    this.itemsViewedTarget.textContent =
      parseInt(this.itemsViewedTarget.textContent) +
      nextPageItemElements.length;

    if (!this.#shouldUsePage(this.#getPage("next"))) {
      this.loadMoreButtonTarget.remove();
    }

    requestIdleCallback(() => {
      this.#fetchPage("next");
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === "previous";

    const currentPage = parseInt(this.productGridTarget?.dataset.currentPage);
    const page = isPrevious ? currentPage - 1 : currentPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.hash = "";

    return {
      page,
      url,
    };
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === "next") {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("page", pageNumber.toString());
      newUrl.hash = "";
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const grid = this.productGridTarget;
    const sectionId = grid?.dataset.sectionId;

    const pageContent = await sectionRenderer.getSectionHTML(
      sectionId,
      true,
      pageInfo.url,
    );

    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const grid = this.productGridTarget;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage))
      return false;

    return true;
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, "text/html");
    const gridElement = parsedPage.querySelector(
      '[data-pagination-target="productGrid"]',
    );
    if (!gridElement) return;

    return gridElement.querySelectorAll('[ref="cards[]"]');
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const grid = this.productGridTarget;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.element.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        // this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage("next");
      }
    });

    // Observe the grid for changes
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ["data-last-page"],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}

Stimulus.register("pagination", PaginationController);
